import React, { useMemo } from 'react';
import { Flex, Spin, Table, Tag, Tooltip, Typography } from '@duxton-web/ui';
import type { ColumnsType } from '@duxton-web/ui/es/table';
import { useQueries } from '@tanstack/react-query';

import type {
  GetMetricScorecardResponse,
  RawScorecardData,
  ScorecardMetricRow,
  ScorecardSummary,
} from '../../types';
import {
  calculateRelativeUplift,
  formatAbsoluteDelta,
  formatConfidenceInterval,
  formatMetricMean,
  formatPValue,
  formatRelativeDelta,
  getDeltaColor,
  getMetricLevelTag,
  getMetricScorecardErrorMessage,
  getMidasMetricUrl,
  getSignificanceLabel,
  isNewMetric,
  isPercentageMetric,
} from '../../utils';
import type {
  ExperimentAnalysisConfig,
  MetricWithDimensionFields,
} from '@/features/experiment/constants/analysis';
import { queryKeys } from '@/hooks/constants';
import Service from '@/lib/App';

interface Props {
  experimentName: string;
  metrics: ScorecardMetricRow[];
  enabled: boolean;
  isExperimentCompleted: boolean;
  analysisConfig?: ExperimentAnalysisConfig;
}

interface TreatmentSummaryCell {
  controlMean?: number;
  treatmentMean?: number;
  uplift: number | null;
  pValue: number | null;
  ciLower?: number;
  ciUpper?: number;
  isStale: boolean;
}

interface SummaryRow {
  key: string;
  metric: ScorecardMetricRow;
  metricUnit?: string;
  summaries: Record<number, TreatmentSummaryCell>;
  errorMessage?: string;
}

const getConfiguredMetrics = (
  analysisConfig?: ExperimentAnalysisConfig,
): MetricWithDimensionFields[] => [
  ...(analysisConfig?.primary_metrics ?? []),
  ...(analysisConfig?.secondary_metrics ?? []),
  ...(analysisConfig?.guardrail_metrics ?? []),
];

const renderStackedDelta = ({
  absoluteDelta,
  relativeDelta,
  statisticsLabel,
  tone,
}: {
  absoluteDelta: string;
  relativeDelta: string;
  statisticsLabel?: string;
  tone: string;
}) => (
  <Flex vertical align="flex-end" gap={0}>
    <Typography.Text strong style={{ color: tone }}>
      {absoluteDelta}
    </Typography.Text>
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {relativeDelta}
    </Typography.Text>
    {statisticsLabel && (
      <Typography.Text
        type="secondary"
        style={{ fontSize: 12, whiteSpace: 'nowrap' }}
      >
        {statisticsLabel}
      </Typography.Text>
    )}
  </Flex>
);

const SummaryView: React.FC<Props> = ({
  experimentName,
  metrics,
  enabled,
  isExperimentCompleted,
  analysisConfig,
}) => {
  const eligibleMetrics = useMemo(
    () => metrics.filter((metric) => metric.status !== 'NOT_STARTED'),
    [metrics],
  );

  const metricConfigByIdentity = useMemo(() => {
    const metricConfigMap = new Map<string, MetricWithDimensionFields>();

    getConfiguredMetrics(analysisConfig).forEach((metric) => {
      if (metric.metric_id) {
        metricConfigMap.set(metric.metric_id, metric);
      }
      if (metric.name) {
        metricConfigMap.set(metric.name, metric);
      }
    });

    return metricConfigMap;
  }, [analysisConfig]);

  const metricQueries = useQueries({
    queries: eligibleMetrics.map((metric) => ({
      queryKey: queryKeys.planner.metricScorecard(
        experimentName,
        metric.metricId,
      ),
      queryFn: () =>
        Service.planner.getMetricScorecard(experimentName, metric.metricId),
      enabled,
      staleTime: 60 * 1000,
    })),
  });

  const isLoading =
    enabled &&
    eligibleMetrics.length > 0 &&
    metricQueries.some((query) => query.isLoading);

  const rows = useMemo<SummaryRow[]>(() => {
    const queryMap = new Map(
      eligibleMetrics.map((metric, index) => [
        metric.metricId,
        metricQueries[index],
      ]),
    );

    return metrics.map((metric) => {
      const query = queryMap.get(metric.metricId);
      const data = query?.data as GetMetricScorecardResponse | undefined;
      const metricConfig =
        metricConfigByIdentity.get(metric.metricId) ??
        metricConfigByIdentity.get(metric.metricKey);

      if (!data || !data.scorecard) {
        let errorMessage: string | undefined;
        if (query?.error) {
          errorMessage = getMetricScorecardErrorMessage(query.error);
        } else if (metric.status === 'NOT_STARTED') {
          errorMessage = 'Waiting for the first successful run.';
        }

        return {
          key: metric.metricId,
          metric,
          metricUnit: metricConfig?.metric_unit,
          summaries: {},
          errorMessage,
        };
      }

      const summaries = (data.scorecard ?? []).reduce<
        Record<number, TreatmentSummaryCell>
      >((acc, scorecard: ScorecardSummary) => {
        const raw = scorecard.raw as RawScorecardData | null;
        const dataInfo = raw?.data_info;
        const controlMean =
          dataInfo?.treatment_0?.mean_w ?? dataInfo?.treatment_0?.mean;
        // API always stores treatment data under treatment_1 in raw data
        const treatmentData = dataInfo?.treatment_1;

        acc[scorecard.treatmentId] = {
          controlMean,
          treatmentMean: treatmentData?.mean_w ?? treatmentData?.mean,
          uplift: scorecard.uplift ?? null,
          pValue: scorecard.pValue ?? null,
          ciLower: scorecard.confidenceInterval?.[0]?.value,
          ciUpper: scorecard.confidenceInterval?.[1]?.value,
          isStale: data.isPreviousRun,
        };

        return acc;
      }, {});

      return {
        key: metric.metricId,
        metric,
        metricUnit: metricConfig?.metric_unit,
        summaries,
      };
    });
  }, [eligibleMetrics, metricConfigByIdentity, metricQueries, metrics]);

  const treatmentIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows.flatMap((row) =>
            Object.keys(row.summaries).map((treatmentId) =>
              Number(treatmentId),
            ),
          ),
        ),
      ).sort((left, right) => left - right),
    [rows],
  );

  const columns = useMemo<ColumnsType<SummaryRow>>(() => {
    const treatmentColumnCount = isExperimentCompleted ? 3 : 2;
    const baseColumns: ColumnsType<SummaryRow> = [
      {
        title: 'Priority',
        key: 'priority',
        width: 110,
        render: (_value, row) => {
          const { label, color } = getMetricLevelTag(row.metric.metricLevel);
          return <Tag color={color}>{label}</Tag>;
        },
      },
      {
        title: 'Metric',
        key: 'metric',
        width: 300,
        render: (_value, row) => (
          <Flex vertical gap={4}>
            <Flex align="center" gap={8}>
              <Typography.Link
                strong
                href={getMidasMetricUrl(row.metric.metricKey)}
                target="_blank"
                rel="noreferrer"
              >
                {row.metric.metricDisplayName}
              </Typography.Link>
              {isNewMetric(row.metric.createdAt) && (
                <Tag color="success" style={{ margin: 0 }}>
                  New
                </Tag>
              )}
            </Flex>
            {Object.values(row.summaries).some(
              (summary) => summary.isStale,
            ) && <Tag color="gold">Showing previous successful run</Tag>}
          </Flex>
        ),
      },
      {
        title: 'Control',
        key: 'control',
        width: 120,
        align: 'right',
        onCell: (row) => {
          if (row.errorMessage) {
            return {
              colSpan: 1 + treatmentIds.length * treatmentColumnCount,
            };
          }
          return {};
        },
        render: (_value, row) => {
          if (row.errorMessage) {
            return (
              <Flex
                align="center"
                justify="center"
                style={{ width: '100%', textAlign: 'center' }}
              >
                <Typography.Text type="secondary" italic>
                  {row.errorMessage}
                </Typography.Text>
              </Flex>
            );
          }

          const firstSummary = Object.values(row.summaries)[0];
          const shouldFormatAsPercentage = isPercentageMetric({
            metricUnit: row.metricUnit,
          });

          return (
            <Typography.Text>
              {formatMetricMean(
                firstSummary?.controlMean,
                shouldFormatAsPercentage,
              )}
            </Typography.Text>
          );
        },
      },
    ];

    const treatmentColumns: ColumnsType<SummaryRow> = treatmentIds.map(
      (treatmentId) => ({
        title: `Treatment ${treatmentId}`,
        key: `treatment-${treatmentId}`,
        children: [
          {
            title: 'Value',
            key: `treatment-${treatmentId}-value`,
            width: 120,
            align: 'right',
            onCell: (row) => (row.errorMessage ? { colSpan: 0 } : {}),
            render: (_value, row) => {
              const summary = row.summaries[treatmentId];
              const shouldFormatAsPercentage = isPercentageMetric({
                metricUnit: row.metricUnit,
              });

              return (
                <Typography.Text>
                  {formatMetricMean(
                    summary?.treatmentMean,
                    shouldFormatAsPercentage,
                  )}
                </Typography.Text>
              );
            },
          },
          {
            title: 'Δ',
            key: `treatment-${treatmentId}-delta`,
            width: isExperimentCompleted ? 230 : 150,
            align: 'right',
            onCell: (row) => (row.errorMessage ? { colSpan: 0 } : {}),
            render: (_value, row) => {
              const summary = row.summaries[treatmentId];
              const shouldFormatAsPercentage = isPercentageMetric({
                metricUnit: row.metricUnit,
              });
              const absoluteDelta = formatAbsoluteDelta({
                controlMean: summary?.controlMean,
                treatmentMean: summary?.treatmentMean,
                isPercentageMetric: shouldFormatAsPercentage,
              });
              const relativeDelta = formatRelativeDelta(
                calculateRelativeUplift(
                  summary?.controlMean,
                  summary?.treatmentMean,
                ),
              );
              const confidenceInterval = formatConfidenceInterval({
                ciLower: summary?.ciLower,
                ciUpper: summary?.ciUpper,
                isPercentageMetric: shouldFormatAsPercentage,
              });
              const statisticsLabel = isExperimentCompleted
                ? `p-value ${formatPValue(summary?.pValue)} · CI ${confidenceInterval}`
                : undefined;
              const tone = isExperimentCompleted
                ? getDeltaColor(
                    summary?.treatmentMean !== undefined &&
                      summary?.controlMean !== undefined
                      ? summary.treatmentMean - summary.controlMean
                      : null,
                  )
                : '#595959';

              return (
                <Tooltip
                  title={
                    isExperimentCompleted
                      ? undefined
                      : `p-value ${formatPValue(summary?.pValue)} · CI ${confidenceInterval}`
                  }
                >
                  <div>
                    {renderStackedDelta({
                      absoluteDelta,
                      relativeDelta,
                      statisticsLabel,
                      tone,
                    })}
                  </div>
                </Tooltip>
              );
            },
          },
          ...(isExperimentCompleted
            ? [
                {
                  title: 'Sig?',
                  key: `treatment-${treatmentId}-sig`,
                  width: 110,
                  align: 'center' as const,
                  onCell: (row) => (row.errorMessage ? { colSpan: 0 } : {}),
                  render: (_value, row) => {
                    const summary = row.summaries[treatmentId];
                    const sig = getSignificanceLabel(
                      summary?.pValue ?? null,
                      treatmentIds.length,
                    );
                    return <Tag color={sig.color}>{sig.label}</Tag>;
                  },
                },
              ]
            : []),
        ],
      }),
    );

    return [...baseColumns, ...treatmentColumns];
  }, [isExperimentCompleted, treatmentIds]);

  if (!enabled) {
    return null;
  }

  if (isLoading) {
    return (
      <Flex align="center" justify="center" gap={8} style={{ padding: 32 }}>
        <Spin size="small" />
        <Typography.Text type="secondary">
          Building stakeholder summary view…
        </Typography.Text>
      </Flex>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={rows}
      pagination={false}
      size="small"
      scroll={{ x: 'max-content' }}
    />
  );
};

export default SummaryView;
